import { Suspense, useDeferredValue, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    changeLocation,
    getLocationSelection,
    getSelectionsFromSeries,
    selectDivision,
    selectFilter,
    selectSeries
} from "./helpers";
import { useOrganisation } from "components/providers/OrganisationProvider";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ButtonWithMenu } from "components/layout/buttons/ButtonWithMenu";
import { Select } from "components/layout/forms/fields/Select";
import { NewRankingsTable } from "components/layout/series/rankings/NewRankingsTable";
import { T } from "components/util/t";
import { SkeletonTable } from "components/layout/skeleton/SkeletonTable";
import { Box, Stack, Typography } from "@mui/material";
import { SearchForm } from "components/search/search";
import { useSearch } from "../../../../hooks/useListFilter";
import { makeStyles } from "components/providers/makeStyles";
import { RankingNotFoundNotification } from "../../../layout/series/rankings/RankingNotFoundNotification";
import { RankingsFilterButton } from "./RankingsFilterButton";

const mapOptions = option => ({ label: option.name, value: option.id });

const useStyles = makeStyles(theme => ({
    athleteSearch: {
        [theme.breakpoints.up("md")]: {
            width: "220px"
        }
    }
}));

const SeriesDivisionSelectorButton = ({ selectedDivision, selectedSeries, seriesOptions, location, history, divisions }) =>
    <ButtonWithMenu title={selectedDivision.name} subtitle={selectedSeries.name}>
        <div>
            <Typography variant="label1" component="label" marginBottom={1} display="block" htmlFor="series-selector">
                <T>Series</T>
            </Typography>
            <Select
                field={{ options: seriesOptions.map(mapOptions), inputProps: { id: "series-selector" } }}
                input={{
                    value: selectedSeries.id,
                    onChange: selectSeries(location, history, seriesOptions)
                }}
            />
        </div>
        <div>
            <Typography variant="label1" component="label" marginBottom={1} display="block" htmlFor="division-selector">
                <T>Division</T>
            </Typography>
            <Select
                field={{ options: divisions.map(mapOptions), inputProps: { id: "division-selector" } }}
                input={{ value: selectedDivision.id, onChange: selectDivision(location, history, seriesOptions) }}
            />
        </div>
    </ButtonWithMenu>;

export const NewRankingsPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const organisation = useOrganisation();
    const { seriesOptions, selectedSeries, divisions, selectedDivision, searchedFound, filter } = getSelectionsFromSeries(organisation.series, false, location);

    const {
        searchString,
        onSearch,
        clearSearch
    } = useSearch();
    const athleteSearchTerm = useDeferredValue(searchString);
    const filterProperty = selectedSeries?.rankingOptions?.filters?.property;

    useEffect(() => {
        const { searchSeries, searchDivision } = getLocationSelection(location);
        clearSearch(true);
        if (!searchSeries || !searchDivision) changeLocation(selectedSeries.id, selectedDivision.id, history.replace);
        if (selectedSeries.id === undefined) return history.replace(`/${organisation.shortName}`);
    }, [selectedDivision, selectedSeries]);

    return (selectedSeries.id && <>
        <RankingNotFoundNotification rankingNotFound={!searchedFound} />

        <Box display={{ md: "flex" }} alignItems="flex-end" justifyContent="space-between" flexWrap="wrap">
            <SeriesDivisionSelectorButton
                selectedDivision={selectedDivision}
                selectedSeries={selectedSeries}
                seriesOptions={seriesOptions}
                location={location}
                history={history}
                divisions={divisions}
            />
            <Stack spacing={2} mt={1} mb={{ xs: 3, md: 0 }} direction={{ xs: "column", md: "row" }}>
                {filterProperty && !filterProperty?.value &&
                <RankingsFilterButton filter={filter} setFilter={selectFilter(location, history)} seriesId={selectedSeries.id} divisionId={selectedDivision.id} label={selectedSeries.rankingOptions.filters.property.label}/>}
                <SearchForm className={classes.athleteSearch} placeholder="Athlete" search={searchString} onChange={onSearch} loading={searchString !== athleteSearchTerm}/>
            </Stack>
        </Box>

        <Box marginTop={2} marginBottom={3}>
            <Suspense fallback={<SkeletonTable/>}>
                <NewRankingsTable selectedSeries={selectedSeries} selectedDivision={selectedDivision} athleteSearchTerm={athleteSearchTerm} filter={filter}/>
            </Suspense>
        </Box>
    </>
    );
};

export default NewRankingsPage;
